import {
	type LeadPipelineConfig,
	LeadPipelineType,
	LeadsTableTab,
	LeadStageType,
	PreQualificationStatus,
} from '@ekhodealer/ekho-common';

/**
 * Computes preQualStatus facet counts with PENDING added.
 * Algolia does not facet on empty/undefined preQualStatus, so PENDING is derived as:
 * total - PRE_QUALIFIED - REJECTED
 */
export function computePreQualStatusCounts(
	preQualStatus: Record<string, number> | undefined,
	total: number,
): Record<string, number> {
	const status = preQualStatus ?? {};
	const preQualified = status[PreQualificationStatus.PRE_QUALIFIED] ?? 0;
	const rejected = status[PreQualificationStatus.REJECTED] ?? 0;
	const pending = Math.max(0, total - preQualified - rejected);
	return { ...status, PENDING: pending };
}

/**
 * Maps the current leads table tab to the corresponding pipeline config.
 */
export function pipelineConfigFromLeadsTab(
	selectedTab: LeadsTableTab,
	pipelineConfigMap: Record<string, LeadPipelineConfig>,
): LeadPipelineConfig | null {
	let pipelineType: string | null = null;
	switch (selectedTab) {
		case LeadsTableTab.INBOUND:
			pipelineType = LeadPipelineType.INBOUND_PIPELINE;
			break;
		case LeadsTableTab.DIGITAL_RETAIL:
			pipelineType = LeadPipelineType.DIGITAL_RETAIL_PIPELINE;
			break;
		case LeadsTableTab.ALL: // Leads from different pipelines in the same board are not supported
		default:
			pipelineType = null;
			break;
	}

	if (!pipelineType || !pipelineConfigMap[pipelineType]) {
		return null;
	}

	return pipelineConfigMap[pipelineType];
}

/**
 * Map lead stage type (enum) to Tag accent color.
 * Used by StageTag, StageCell, StageActionMenu, and LeadsKanbanBoard for consistent styling.
 */
export const getStageAccent = (
	stageType: LeadStageType | undefined,
): 'neutral' | 1 | 2 | 3 | 4 | 5 | 6 | 7 => {
	if (!stageType) return 'neutral';

	switch (stageType) {
		case LeadStageType.NEW:
			return 7; // Green
		case LeadStageType.TO_REACH_OUT:
			return 6; // Yellow
		case LeadStageType.CONTACTED:
			return 3; // Teal
		case LeadStageType.ENGAGED:
			return 2; // Purple
		case LeadStageType.ON_HOLD:
			return 5; // Orange
		case LeadStageType.NEEDS_ATTENTION:
			return 4; // cherryRed
		case LeadStageType.BOOKED:
			return 1; // cobaltBlue
		case LeadStageType.CLOSED_WON:
			return 7; // Green
		case LeadStageType.CLOSED_LOST:
			return 'neutral'; // Gray
		default:
			return 'neutral';
	}
};
